import { ref, watchEffect } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'acme.theme'

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

const preference = ref<ThemePreference>(readPreference())
// Whether the person picked light/dark here (then the tenant's initial theme no longer applies).
const chosen = ref(preference.value !== 'system')
const systemDark = ref(typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)

if (typeof window !== 'undefined') {
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', (event) => {
    systemDark.value = event.matches
  })
}

// Light/dark follows the system unless the person picks one (a per-browser UI preference, not
// session data). Applied as data-theme on <html>, which the design tokens read.
export function useTheme() {
  watchEffect(() => {
    const root = document.documentElement
    if (preference.value === 'system') {
      root.removeAttribute('data-theme')
    } else {
      root.setAttribute('data-theme', preference.value)
    }
  })

  const isDark = () => (preference.value === 'system' ? systemDark.value : preference.value === 'dark')

  function toggle() {
    preference.value = isDark() ? 'light' : 'dark'
    chosen.value = true
    try {
      localStorage.setItem(STORAGE_KEY, preference.value)
    } catch {
      // Storage unavailable (private mode): the choice lasts for this page only.
    }
  }

  // The tenant theme's "Tema inicial": what a person sees until they pick one themselves.
  function setInitial(initial: ThemePreference) {
    if (!chosen.value) preference.value = initial
  }

  return { preference, isDark, toggle, setInitial }
}
